import { DashboardOutlined, FileAddOutlined } from '@ant-design/icons';
import { getDealRoomInfoExternalFiles, getDealRoomInfoFiles, getDealRoomUnreadFile } from '@app/api/deal-room-info.api';
import { getDealRoomPeopleSharableUrl, updateDealRoomPeopleTime } from '@app/api/deal-room-people.api';
import {
  getClientReview,
  getDealRoomOnlinePeople,
  getDealRoomOnlinePeopleExternal,
  getDealRoomShortUrl,
  readDealRoom,
  updateDealRoomSections,
} from '@app/api/deal-room.api';
import { Panel } from '@app/components/common/Collapse/Collapse';
import { ShareOutLineIcon } from '@app/components/common/icons/ShareOutLineIcon';
import { ChooseDisabledMenu } from '@app/components/common/Modal/ChooseDisabledMenu/ChooseDisabledMenu';
import { DealRoomUploadFileModal } from '@app/components/common/Modal/DealRoomUploadFileModal/DealRoomUploadFileModal';
import { Spinner } from '@app/components/common/Spinner/Spinner';
import { UserAvatar } from '@app/components/common/UserAvatar/UserAvatar';
import { Dates } from '@app/constants/Dates';
import { dealRoomDisabledSectionFiles } from '@app/constants/dealRoom';
import { DealRoomDisabledSection, DealRoomSection, DealRoomStatus } from '@app/constants/enums/deal-room';
import {
  ADMIN_BASE_BATH,
  DATE_TIME_FORMAT,
  SOCKET_DEAL_ROOM_ATTACHMENT,
  SOCKET_DEAL_ROOM_MESSAGE_FOR_ADMIN,
  SOCKET_DEAL_ROOM_ONLINE_USERS,
  SOCKET_DEAL_ROOM_SYNC_ONLINE_USERS,
  SOCKET_DEAL_ROOM_UNREAD,
} from '@app/constants/variables';
import { notificationController } from '@app/controllers/notificationController';
import { useAppSelector } from '@app/hooks/reduxHooks';
import {
  IDealRoomDocument,
  IDealRoomComparisonSection,
  IDealRoomMenuSection,
  IDealRoomMessage,
  IDealRoomOfferSection,
  IDealRoomSection,
  IDealRoomSectionContent,
  IDealRoomShare,
} from '@app/interfaces/deal-room';
import { IDealRoomInfoFile } from '@app/interfaces/deal-room-info';
import { IDealRoomPeople } from '@app/interfaces/deal-room-people';
import socket from '@app/utils/socket';
import { getAdminNotificationMessageKeyOnChat, getTabId } from '@app/utils/utils';
import { Avatar, notification, Popover, Tooltip } from 'antd';
import appLogo from 'assets/logo.jpg';
import mainLogo from 'assets/main-logo.svg';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { AgentBannerCard } from './AgentBannerCard/AgentBannerCard';
import { SectionNavBar, getIcon } from './SectionNavBar/SectionNavBar';
import { scrollToSection } from '@app/utils/scrollToSection';
import DealRoomFooter from './DealFooter/DealRoomFooter';
import { DealRoomChatSidebar } from './DealRoomChatSidebar/DealRoomChatSidebar';
import { DealRoomInsuranceCard } from './DealRoomInsuranceCard/DealRoomInsuranceCard';
import * as S from './DealRoomLanding.styles';
import { DealRoomMenu } from './DealRoomMenu/DealRoomMenu';
import DealRoomMessageCard from './DealRoomMessageCard/DealRoomMessageCard';
import { Attachment } from './DealRoomSection/Attachment/Attachment';
import { Earthquake } from './DealRoomSection/Earthquake';
import { ElectricChargingStation } from './DealRoomSection/ElectricChargingStation';
import { EmployersLiability } from './DealRoomSection/EmployersLiability';
import { InsuranceCoverageSharedBuilding } from './DealRoomSection/InsuranceCoverageSharedBuilding';
import { Comparisons } from './DealRoomSection/Comparisons';
import { ListenToOurPodcast } from './DealRoomSection/ListenToOurPodcast/ListenToOurPodcast';
import { ListenToOurPodcasts } from './DealRoomSection/ListenToOurPodcast/ListenToOurPodcasts';
import { Opening } from './DealRoomSection/Opening';
import { PersonalClaimsForCommitteeMember } from './DealRoomSection/PersonalClaimsForCommitteeMember';
import { PriceQuote } from './DealRoomSection/PriceQuote/PriceQuote';
import { SharedBuildingContent } from './DealRoomSection/SharedBuildingContent';
import { SharedBuildingStructure } from './DealRoomSection/SharedBuildingStructure';
import { ThirdPartyInsurance } from './DealRoomSection/ThirdPartyInsurance';
import { useNavigate } from 'react-router-dom';
import { MedianCoBrowse } from './MedianCoBrowse/MedianCoBrowse';
import {
  getDisplayMenuSections,
  getFilteredOfferSectionSections,
  getMappedAdvancedOfferSections,
  getMappedMenuSections,
  getMappedOfferSections,
  getMappedSections,
  getMappedComparisonSections,
  getMenuSections,
  getSaveMenuSections,
} from '@app/utils/dealRoom';
import { DynamicPriceQuote } from './DealRoomSection/PriceQuote/DynamicPriceQuote';
import { AccessibilityWidget } from './AccessibilityWidget/AccessibilityWidget';
import { Document } from './DealRoomSection/Document';
import { getDealRoomStatus } from '@app/utils/options';
import { DealRoomRemarkModal } from './DealRoomRemarkModal/DealRoomRemarkModal';
import { MouseflowWidget } from './MouseflowWidget/MouseflowWidget';
import { TrackingScripts } from './TrackingScripts/TrackingScripts';
import {
  MF_LABELS,
  mfElementLabel,
  mfTag,
  mfTrackAction,
  mfTrackSection,
  mfTrackSectionClosed,
} from '@app/utils/mouseflowTracker';
import { MouseflowDetailsModal } from '@app/components/common/Modal/MouseflowDetailsModal/MouseflowDetailsModal';
import { LEVEL_COLORS } from '@app/components/common/Modal/MouseflowDetailsModal/MouseflowDetailsModal.styles';
import { LevelType } from '@app/constants/enums/mouseflow-level';
import { SmartChoice } from './DealRoomSection/SmartChoice';
import { DescriptionText } from '@app/components/common/DescriptionText/DescriptionText';

const ONLINE_USER_TIMEOUT = 30000;

export const sectionContentMap: Record<
  DealRoomSection,
  (
    section?: IDealRoomSectionContent,
    dealRoom?: IDealRoomShare,
    internalFiles?: IDealRoomInfoFile[],
    externalFiles?: IDealRoomInfoFile[],
    people?: IDealRoomPeople | null,
    onDeleteSuccess?: (isExternal: boolean) => void,
    activeKey?: string,
  ) => JSX.Element
> = {
  [DealRoomSection.OPENING]: () => <Opening />,
  [DealRoomSection.INSURANCE_COVERAGE_SHARED_BUILDING]: (section) => (
    <InsuranceCoverageSharedBuilding section={section as IDealRoomSectionContent} />
  ),
  [DealRoomSection.THIRD_PARTY_INSURANCE]: () => <ThirdPartyInsurance />,
  [DealRoomSection.PERSONAL_CLAIMS_FOR_COMMITTEE_MEMBERS]: () => <PersonalClaimsForCommitteeMember />,
  [DealRoomSection.EMPLOYERS_LIABILITY]: () => <EmployersLiability />,
  [DealRoomSection.SHARED_BUILDING_STRUCTURE]: () => <SharedBuildingStructure />,
  [DealRoomSection.EARTHQUAKE]: () => <Earthquake />,
  [DealRoomSection.SHARED_BUILDING_CONTENTS]: () => <SharedBuildingContent />,
  [DealRoomSection.ELECTRIC_CHARGING_STATION]: () => <ElectricChargingStation />,
  [DealRoomSection.COMPARISONS]: (section) => <Comparisons section={section as IDealRoomSectionContent} />,
  [DealRoomSection.DOCUMENTS]: (section) => <Document section={section as IDealRoomSectionContent} />,
  [DealRoomSection.LISTEN_TO_OUR_PODCASTS]: (section, dealRoom) => (
    <ListenToOurPodcasts section={section as IDealRoomSectionContent} dealRoom={dealRoom} />
  ),
  [DealRoomSection.PODCAST_INSURANCE]: (_section, dealRoom) => (
    <ListenToOurPodcast type={DealRoomSection.PODCAST_INSURANCE} dealRoom={dealRoom} />
  ),
  [DealRoomSection.PODCAST_LEGAL]: (_section, dealRoom) => (
    <ListenToOurPodcast type={DealRoomSection.PODCAST_LEGAL} dealRoom={dealRoom} />
  ),
  [DealRoomSection.PODCAST_MANAGEMENT_APP]: (_section, dealRoom) => (
    <ListenToOurPodcast type={DealRoomSection.PODCAST_MANAGEMENT_APP} dealRoom={dealRoom} />
  ),
  [DealRoomSection.PODCAST_FIRE_SAFETY]: (_section, dealRoom) => (
    <ListenToOurPodcast type={DealRoomSection.PODCAST_FIRE_SAFETY} dealRoom={dealRoom} />
  ),
  [DealRoomSection.PODCAST_MANAGEMENT_COMPANIES]: (_section, dealRoom) => (
    <ListenToOurPodcast type={DealRoomSection.PODCAST_MANAGEMENT_COMPANIES} dealRoom={dealRoom} />
  ),
  [DealRoomSection.PODCAST_MAINTENANCE]: (_section, dealRoom) => (
    <ListenToOurPodcast type={DealRoomSection.PODCAST_MAINTENANCE} dealRoom={dealRoom} />
  ),
  [DealRoomSection.PODCAST_NEW_BUILDINGS_FORM_4]: (_section, dealRoom) => (
    <ListenToOurPodcast type={DealRoomSection.PODCAST_NEW_BUILDINGS_FORM_4} dealRoom={dealRoom} />
  ),
  [DealRoomSection.SMART_CHOICE]: () => <SmartChoice />,
  [DealRoomSection.ATTACHMENTS]: (
    _section?: IDealRoomSectionContent,
    dealRoom?: IDealRoomShare,
    internalFiles?: IDealRoomInfoFile[],
    externalFiles?: IDealRoomInfoFile[],
    people?: IDealRoomPeople | null,
    onDeleteSuccess?: (isExternal: boolean) => void,
  ) => (
    <Attachment
      dealRoom={dealRoom}
      internalFiles={internalFiles}
      externalFiles={externalFiles}
      people={people}
      onDeleteSuccess={onDeleteSuccess}
    />
  ),
  [DealRoomSection.DECIDED_TO_PURCHASE_THE_INSURANCE]: () => <></>,
};

interface DealRoomLandingProps {
  dealRoom: IDealRoomShare;
  homeImg: string;
  people: IDealRoomPeople | null;
}

export const DealRoomLanding: React.FC<DealRoomLandingProps> = ({ dealRoom, homeImg, people }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user.user);
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [internalFiles, setInternalFiles] = useState<IDealRoomInfoFile[]>([]);
  const [externalFiles, setExternalFiles] = useState<IDealRoomInfoFile[]>([]);
  const [loader, setLoader] = useState(false);
  const apiIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef(Date.now());
  const hasSentData = useRef(false);
  const faviconTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasUnreadRef = useRef(false);
  const originalFaviconHref = useRef<string | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<IDealRoomPeople[]>([]);
  const [visibleUsers, setVisibleUsers] = useState<IDealRoomPeople[]>([]);
  const [extraUsers, setExtraUsers] = useState<IDealRoomPeople[]>([]);
  const [scrolled, setScrolled] = useState(false);
  const [sections, setSections] = useState<IDealRoomSection[]>(dealRoom?.sections || []);
  const [offerSections, setOfferSections] = useState<IDealRoomOfferSection[]>([]);
  const [mainOffer, setMainOffer] = useState<IDealRoomDocument[]>([]);
  const [menuSections, setMenuSections] = useState<IDealRoomMenuSection[]>([]);
  const [comparisonSections, setComparisonSections] = useState<IDealRoomComparisonSection[]>(
    dealRoom?.comparisonSections || [],
  );
  const [activeSections, setActiveSections] = useState<IDealRoomSectionContent[]>([]);
  const [documentModal, setDocumentModal] = useState(false);
  const [chatMessage, setChatMessage] = useState(0);
  const [defaultChannel, setDefaultChannel] = useState<string | null>(null);
  const [insuranceCard, setInsuranceCard] = useState<IDealRoomSection | null>(null);
  const [isDealRoomClosed, setIsDealRoomClosed] = useState(false);
  const [unreadFiles, setUnreadFiles] = useState<number>(0);

  const [isChatSidebarOpen, setChatSidebarOpen] = useState(false);
  const [clientReview, setClientReview] = useState<string>('');
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [menuModal, setMenuModal] = useState(false);

  // Every panel currently open. The collapse is no longer an accordion, so this
  // is the authoritative state; `activeKey` below only names the latest of them.
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  // The most recently opened section. Scrolling, section tracking and the
  // attachments refresh each want a single section, which "the open one" stopped
  // identifying once several can be open at once.
  const [activeKey, setActiveKey] = useState<string>('');
  // Scroll requests, tokened so that asking twice for the same section still
  // fires. See openSection for why re-requesting the same key has to work.
  const [scrollTarget, setScrollTarget] = useState<{ key: string; token: number } | null>(null);
  const scrollTokenRef = useRef(0);

  const prevOpenKeysRef = useRef<string[]>([]);
  // The load-time scroll to the default section must fire only once.
  const defaultAppliedRef = useRef(false);
  // Documents and Comparisons start expanded; seeded once, when sections arrive.
  const defaultOpenAppliedRef = useRef(false);
  /**
   * The landing scroll waits for the page to stop growing before it moves, which
   * takes a few seconds on a cold load. Without this the room just sits there
   * looking finished and then lurches, so the wait is shown rather than hidden.
   */
  const [landingPending, setLandingPending] = useState(false);
  const prevChatOpenRef = useRef<boolean>(false);

  const [mouseflowModalOpen, setMouseflowModalOpen] = useState(false);

  const onSelectMenu = (newItems: IDealRoomSectionContent[]) => {
    const newCategories = newItems.map((n) => n.category).filter((c) => !!c) as string[];
    const allSections = getMenuSections(sections, offerSections, menuSections, !!user, comparisonSections, t);
    const totalSections = [...allSections, ...newItems];
    const uniqueSections = totalSections.filter(
      (section, index, self) => index === self.findIndex((s) => s.key === section.key),
    ) as IDealRoomSectionContent[];
    onUpdateSections(
      uniqueSections,
      false,
      newItems.map((n) => n.key),
      newCategories,
    );
  };

  const onRemoveMenu = (menu: string) => {
    const allSections = getMenuSections(sections, offerSections, menuSections, !!user, comparisonSections, t);
    const totalSections = allSections.filter((s) => s.key !== menu);
    const uniqueSections = totalSections.filter(
      (section, index, self) => index === self.findIndex((s) => s.key === section.key),
    ) as IDealRoomSectionContent[];
    onUpdateSections(uniqueSections, false);
  };

  const onUploadSuccess = () => {
    mfTag(MF_LABELS.uploadSuccess);
    setDocumentModal(false);
    if (openKeys.includes(DealRoomSection.ATTACHMENTS)) {
      if (user) {
        syncInternalFiles();
      } else {
        syncExternalFiles();
      }
    }
    setSections(
      sections.map((section) => ({
        ...section,
        highlighted: section.section === DealRoomSection.ATTACHMENTS ? true : section.highlighted,
      })),
    );
  };

  const getUserName = () => {
    return (
      people
        ? `${people?.firstName} ${people?.lastName || ''}`
        : user
        ? `${user?.firstname || ''} ${user?.lastname || ''}`
        : ''
    )?.trim();
  };

  const shareLink = async () => {
    setLoader(true);
    try {
      let response = null;
      if (user) {
        response = await getDealRoomShortUrl(dealRoom?.id as string);
      } else {
        response = await getDealRoomPeopleSharableUrl(dealRoom?.id as string, {
          sessionId: dealRoom?.sessionId as string,
          uniqueId: dealRoom?.uniqueId as string,
        });
      }
      if (response && response.shortUrl) {
        navigator.clipboard.writeText(response.shortUrl);
        mfTag(MF_LABELS.shareLinkCopied);
        notificationController.success({
          message: t('dealRooms.form.dealRoomLinkCopiedSuccessfully'),
        });
      }
    } catch (error) {
      mfTag(MF_LABELS.shareLinkFailed);
      notificationController.error({
        message: t('dealRooms.form.errorCopyingDealRoomLink'),
      });
    } finally {
      setLoader(false);
    }
  };

  const onUpdateSections = async (
    uniqSections: IDealRoomSectionContent[],
    closeMenuModal = true,
    visibleMenu: string[] = [],
    newCategories: string[] = [],
  ) => {
    setLoader(true);
    try {
      const allSections = getSaveMenuSections(uniqSections, sections, newCategories);
      const documents = allSections.find((s) => s.key === DealRoomSection.DOCUMENTS);
      const comparisons = allSections.find((s) => s.key === DealRoomSection.COMPARISONS);
      const data = await updateDealRoomSections(dealRoom?.id as string, {
        sections: allSections
          .filter((s) => !s.isExternal && !s.dealRoomMenu && !s.comparison)
          .map((s) => {
            if (s.key === DealRoomSection.DOCUMENTS || s.key === DealRoomSection.COMPARISONS) {
              return getMappedSections({
                ...s,
                subMenu: [],
              });
            }
            if (s.subMenu && s.subMenu.length > 0) {
              return {
                ...getMappedSections(s),
                subMenu: s.subMenu.map(getMappedSections),
              };
            }

            return getMappedSections({
              ...s,
              display: s.key && visibleMenu.includes(s.key) ? true : s.display,
            });
          }),
        offerSections: (documents?.subMenu || []).map(getMappedOfferSections),
        comparisonSections: (comparisons?.subMenu || []).map((s) =>
          getMappedComparisonSections({
            ...s,
            display: s.key && visibleMenu.includes(s.key) ? true : s.display,
          }),
        ),
        menuSections: uniqSections
          .filter((s) => s.dealRoomMenu)
          .map((s) =>
            getMappedMenuSections({
              ...s,
              display: s.key && visibleMenu.includes(s.key) ? true : s.display,
            }),
          ),
      });
      setSections(data.sections || []);
      setOfferSections(
        getFilteredOfferSectionSections(data.offerSections || []).map((o) =>
          getMappedAdvancedOfferSections({
            ...o,
            labelText: o.defaultLabel,
          }),
        ),
      );
      setMenuSections(data.menuSections || []);
      setComparisonSections(data.comparisonSections || []);
      if (closeMenuModal) setMenuModal(false);
    } catch (error) {
      notificationController.error({ message: (error as Error).message });
    } finally {
      setLoader(false);
    }
  };

  const renderPanelHeader = (
    text: string,
    key: DealRoomSection | DealRoomDisabledSection | string,
    description?: string,
  ): JSX.Element => (
    <S.PanelSection>
      <S.PanelHeaderLeft>
        <S.PanelHeaderIcon>{getIcon(key)}</S.PanelHeaderIcon>
        <S.PanelHeaderText>
          <span>{text}</span>
          {description && <DescriptionText description={description} title={text} />}
        </S.PanelHeaderText>
      </S.PanelHeaderLeft>
      {key === DealRoomSection.ATTACHMENTS && !isDealRoomClosed && (
        <Tooltip title={t('dealRooms.landing.uploadDocument')}>
          <FileAddOutlined
            onClick={(e) => {
              e.stopPropagation();
              setDocumentModal(!documentModal);
            }}
          />
        </Tooltip>
      )}
    </S.PanelSection>
  );

  // Mouseflow: resolve the Hebrew display label for a section key. Uses the
  // same precedence the SectionNavBar uses to render pill labels, so what the
  // customer sees on screen is exactly what gets tagged in Mouseflow.
  const resolveSectionLabel = useCallback(
    (key: string): string => {
      if (!key) return '';
      const sec = activeSections.find((s) => s.key === key);
      if (sec?.label) return sec.label;
      if (sec?.isExternal) return sec.labelKey;
      if (sec?.labelKey) return t(sec.labelKey);
      return key;
    },
    [t, activeSections],
  );

  // Mouseflow: delegate clicks inside accordion content to auto-tag every
  // interactive action. Header clicks are excluded — section opens/closes are
  // tracked separately via activeKey.
  const onAccordionContentClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('.ant-collapse-header')) return;
      const interactive = target.closest<HTMLElement>(
        'a[href], button, [role="button"], [data-mf-track], input[type="submit"], input[type="button"], label[for]',
      );
      if (!interactive) return;
      const sectionLabel = resolveSectionLabel(activeKey);
      const label = mfElementLabel(interactive);
      mfTrackAction(MF_LABELS.sectionClickPrefix, sectionLabel || 'לא-ידוע', label);
    },
    [activeKey, resolveSectionLabel],
  );

  // Mouseflow: delegate clicks on the SectionNavBar pills. The section view
  // itself is already tagged via the activeKey effect — this adds an extra
  // Hebrew nav-click tag so we can tell *how* the user navigated (pill vs
  // accordion vs URL hash).
  const onNavBarClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const interactive = target.closest<HTMLElement>('a[href], button, [role="button"], [data-mf-track]');
    if (!interactive) return;
    mfTag(`${MF_LABELS.navBarClick}:${mfElementLabel(interactive)}`);
  }, []);

  /**
   * Opens a panel without disturbing the ones already open, and without moving
   * the page. Deep links use this for the *parent* of the section they target:
   * scrolling to the parent as well produces a second, visibly different
   * landing a moment before the real one.
   */
  const revealSection = useCallback((key: string) => {
    if (!key) return;
    setOpenKeys((keys) => (keys.includes(key) ? keys : [...keys, key]));
    setActiveKey(key);
  }, []);

  /**
   * Opens a panel and asks for a scroll to it. The request carries a token
   * because the section may already be open — Documents and Comparisons start
   * that way — and keying the scroll off the section alone would then be a
   * no-op for exactly the section the page is supposed to land on.
   */
  const openSection = useCallback(
    (key: string) => {
      if (!key) return;
      revealSection(key);
      scrollTokenRef.current += 1;
      setScrollTarget({ key, token: scrollTokenRef.current });
    },
    [revealSection],
  );

  const onDeleteAttachmentSuccess = (isExternal: boolean) => {
    if (isExternal) {
      syncExternalFiles();
      return;
    }
    syncInternalFiles();
  };

  const getSectionContent = (
    section: IDealRoomSectionContent,
    dealRoom: IDealRoomShare,
    internalFiles: IDealRoomInfoFile[],
    externalFiles: IDealRoomInfoFile[],
  ) => {
    if (section.isCategory) {
      return <Document section={section as IDealRoomSectionContent} />;
    }
    if (section.isExternal) {
      if (!section.file) return null;
      return <PriceQuote open={openKeys.includes(section.key)} file={section.file} />;
    }
    if (section.isAdditional) {
      const dealRoomSection = dealRoomDisabledSectionFiles.find((s) => s.section === section.key);
      if (dealRoomSection) {
        return (
          <PriceQuote
            file={`${window.location.origin}/pdf/${dealRoomSection.file}`}
            pageStart={dealRoomSection.pageStart}
            pageEnd={dealRoomSection.pageEnd}
            open={openKeys.includes(section.key)}
          />
        );
      }
    }
    if (section.dealRoomMenu) {
      return (
        <DynamicPriceQuote
          open={openKeys.includes(section.key)}
          file={(section.dealRoomMenu?.file as string) || ''}
          link={section.dealRoomMenu?.link}
        />
      );
    }
    const content = sectionContentMap[section.key as DealRoomSection];
    return content
      ? content(section, dealRoom, internalFiles, externalFiles, people, onDeleteAttachmentSuccess, activeKey)
      : null;
  };

  const syncInternalFiles = useCallback(async () => {
    setLoader(true);
    try {
      const data = user
        ? await getDealRoomInfoFiles(dealRoom?.id as string, false)
        : await getDealRoomInfoExternalFiles(dealRoom?.id as string, people?.id as string, false, {
            sessionId: dealRoom?.sessionId || null,
            uniqueId: dealRoom?.uniqueId || null,
          });
      if (data) {
        setInternalFiles(data);
      }
    } catch (error) {
      setInternalFiles([]);
    } finally {
      setLoader(false);
    }
  }, [dealRoom?.id, dealRoom?.sessionId, dealRoom?.uniqueId, people?.id, user]);

  const syncExternalFiles = useCallback(async () => {
    setLoader(true);
    try {
      const data = user
        ? await getDealRoomInfoFiles(dealRoom?.id as string, true)
        : await getDealRoomInfoExternalFiles(dealRoom?.id as string, people?.id as string, true, {
            sessionId: dealRoom?.sessionId || null,
            uniqueId: dealRoom?.uniqueId || null,
          });
      if (data) {
        setExternalFiles(data);
      }
    } catch (error) {
      setExternalFiles([]);
    } finally {
      setLoader(false);
    }
  }, [dealRoom?.id, dealRoom?.sessionId, dealRoom?.uniqueId, people?.id, user]);

  // openSection scrolls whether or not the panel was already open, so a nav pill
  // for an open section needs no special case of its own.
  const onChangeActiveKey = (key: string) => openSection(key);

  // The collapse hands back the full set of open panels. Whichever key is new
  // becomes the one that gets scrolled to and tracked; a close leaves none.
  const onCollapseChange = (keys: string | string[]) => {
    const next = Array.isArray(keys) ? keys : [keys];
    const opened = next.find((key) => !openKeys.includes(key)) ?? '';
    setActiveKey(opened);
    setOpenKeys(next);
    if (opened) {
      scrollTokenRef.current += 1;
      setScrollTarget({ key: opened, token: scrollTokenRef.current });
    }
  };

  const getUnreadFile = useCallback(async () => {
    try {
      const data = await getDealRoomUnreadFile(
        dealRoom?.id as string,
        user ? user.id : null,
        people ? people?.id : null,
      );
      setUnreadFiles(data.unreadFiles);
    } catch (error) {
      setUnreadFiles(0);
    }
  }, [dealRoom?.id, people, user]);

  const fetchClientReview = useCallback(async () => {
    try {
      if (user?.id) {
        const data = await getClientReview(dealRoom?.id as string);
        setClientReview(data.remarks || '');
      }
    } catch (error) {
      setClientReview('');
    }
  }, [dealRoom?.id, user]);

  const setFaviconBadge = useCallback((show: boolean) => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    if (!show) {
      link.href = originalFaviconHref.current || '/favicon.png';
      return;
    }
    if (!originalFaviconHref.current) {
      originalFaviconHref.current = link.href;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = '/favicon.png';
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 32, 32);
      ctx.beginPath();
      ctx.arc(24, 8, 6, 0, 2 * Math.PI);
      ctx.fillStyle = '#e53935';
      ctx.fill();
      link.href = canvas.toDataURL('image/png');
    };
  }, []);

  const markDealRoomRead = useCallback(async () => {
    if (!user || !dealRoom?.id) return;
    try {
      await readDealRoom(dealRoom.id as string);
    } catch {}
  }, [user, dealRoom?.id]);

  const triggerReadTimer = useCallback(() => {
    if (faviconTimerRef.current) clearTimeout(faviconTimerRef.current);
    faviconTimerRef.current = setTimeout(() => {
      setFaviconBadge(false);
      hasUnreadRef.current = false;
    }, 5000);
  }, [setFaviconBadge]);

  const handleUnreadMessage = useCallback(() => {
    if (!user) return;
    hasUnreadRef.current = true;
    setFaviconBadge(true);
    if (!document.hidden) {
      markDealRoomRead();
      triggerReadTimer();
    }
  }, [user, setFaviconBadge, markDealRoomRead, triggerReadTimer]);

  const addNotification = useCallback(
    (user, isAdmin, data: IDealRoomMessage) => {
      if (data?.dealRoomId === dealRoom?.id) {
        setChatMessage((prev) => prev + 1);
      }
      let showChat = isAdmin;
      if (!showChat) {
        if (data.responsible.includes(user.id)) showChat = true;
        if (data.createdBy && data.createdBy === user.id) showChat = true;
      }
      if (showChat) {
        notificationController.info({
          message: t('dealRooms.signals.messageInChat'),
          key: data.id,
          description: t(getAdminNotificationMessageKeyOnChat(data), {
            user: data.user,
            dealRoom: data.dealRoom,
          }),
          duration: 10,
          onClick: () => {
            navigate(`${ADMIN_BASE_BATH}/chats/${data?.dealRoomId}?cId=${data.channel}`, { replace: true });
            notification.close(data.id);
          },
        });
      }
    },
    [dealRoom?.id, navigate, t],
  );

  useEffect(() => {
    if (dealRoom?.channels?.length === 0) return;
    const hasUnreadPeopleChannel = dealRoom?.channels?.find((ch) => ch.unread && ch.dealRoomPeople);
    if (hasUnreadPeopleChannel) {
      setChatSidebarOpen(true);
      setDefaultChannel(hasUnreadPeopleChannel.channel);
      return;
    }
    const hasUnread = dealRoom?.channels?.find((ch) => ch.unread && !ch.dealRoomPeople);
    if (hasUnread) {
      setChatSidebarOpen(true);
      setDefaultChannel(hasUnread.channel);
    }
  }, [dealRoom?.channels]);

  const handleHashChange = useCallback(
    (hash: string) => {
      if (!hash) return;
      const hashKey = hash.replace('#', '');
      // 🔹 Find a top-level section that matches directly
      const topLevelMatch = activeSections.find((section) => section.key === hashKey);

      // 🔹 Find a parent section where one of its subMenu items matches
      const parentMatch = activeSections.find((section) => section.subMenu?.some((sub) => sub.key === hashKey));

      if (topLevelMatch) {
        openSection(topLevelMatch.key);
      } else if (parentMatch) {
        // Open the parent, then land on the child itself — one move, not two.
        // Scrolling to the parent as well shows its header first and moves
        // again a moment later; leaving the scroll to the child's own
        // component instead makes the landing a race it does not always win.
        revealSection(parentMatch.key);
        scrollToSection(hashKey);
      }
    },
    // Identity kept stable: the hashchange listener that depends on this must
    // not re-bind — and re-apply the hash — on every open.
    [activeSections, openSection, revealSection],
  );

  useEffect(() => {
    if (openKeys.includes(DealRoomSection.ATTACHMENTS)) {
      syncInternalFiles();
      syncExternalFiles();
      setUnreadFiles(0);
    }
  }, [openKeys, syncExternalFiles, syncInternalFiles]);

  useEffect(() => {
    if (user?.id) return;
    const { id, sessionId, uniqueId } = dealRoom as IDealRoomShare;

    const sendTimeSpent = () => {
      if (hasSentData.current) return; // Prevent duplicate API calls
      hasSentData.current = true;

      const now = Date.now();
      const totalTimeSpent = Math.round(now - startTimeRef.current);

      updateDealRoomPeopleTime(id as string, {
        sessionId,
        uniqueId,
        time: totalTimeSpent,
        tabId: getTabId(),
      }).then(() => {
        startTimeRef.current = now;
        hasSentData.current = false;
      });
    };

    apiIntervalRef.current = setInterval(() => {
      sendTimeSpent();
    }, ONLINE_USER_TIMEOUT);

    // Handle tab close or page hide
    window.addEventListener('beforeunload', () => sendTimeSpent());
    window.addEventListener('pagehide', () => sendTimeSpent());
    window.addEventListener('unload', () => sendTimeSpent());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') sendTimeSpent();
      if (document.hidden) sendTimeSpent();
    });

    return () => {
      if (apiIntervalRef.current) clearInterval(apiIntervalRef.current); // Clear interval
      sendTimeSpent(); // Send final time before unmount
      window.removeEventListener('beforeunload', () => sendTimeSpent());
      window.removeEventListener('pagehide', () => sendTimeSpent());
      window.removeEventListener('unload', () => sendTimeSpent());
      document.removeEventListener('visibilitychange', () => sendTimeSpent());
    };
  }, [user?.id, dealRoom]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Mouseflow: track section opens/closes whenever the open set changes. Single
  // source of truth — covers nav-bar pills, accordion headers, and hash-driven
  // navigation. Hebrew display label is what gets tagged. Several panels can
  // change at once now, so each added and removed key is reported separately.
  useEffect(() => {
    const prev = prevOpenKeysRef.current;
    openKeys.filter((key) => !prev.includes(key)).forEach((key) => mfTrackSection(resolveSectionLabel(key)));
    prev.filter((key) => !openKeys.includes(key)).forEach((key) => mfTrackSectionClosed(resolveSectionLabel(key)));
    prevOpenKeysRef.current = openKeys;
  }, [openKeys, resolveSectionLabel]);

  // Mouseflow: track chat sidebar open/close (covers fixed btn click and the
  // auto-open path triggered by unread channels).
  useEffect(() => {
    const wasOpen = prevChatOpenRef.current;
    if (isChatSidebarOpen && !wasOpen) {
      mfTrackSection(MF_LABELS.chatSectionLabel);
      mfTag(MF_LABELS.chatOpened);
    } else if (!isChatSidebarOpen && wasOpen) {
      mfTag(MF_LABELS.chatClosed);
    }
    prevChatOpenRef.current = isChatSidebarOpen;
  }, [isChatSidebarOpen]);

  // Mouseflow: surface modal opens as discrete Hebrew-tagged events.
  useEffect(() => {
    if (documentModal) mfTag(MF_LABELS.uploadModalOpened);
  }, [documentModal]);
  useEffect(() => {
    if (menuModal) mfTag(MF_LABELS.menuModalOpened);
  }, [menuModal]);
  useEffect(() => {
    if (isReviewOpen) mfTag(MF_LABELS.reviewModalOpened);
  }, [isReviewOpen]);

  useEffect(() => {
    setSections(dealRoom?.sections || []);
  }, [dealRoom?.sections]);

  useEffect(() => {
    setIsDealRoomClosed(dealRoom?.status === DealRoomStatus.CLOSED);
  }, [dealRoom?.status]);

  useEffect(() => {
    getUnreadFile();
  }, [getUnreadFile]);

  useEffect(() => {
    fetchClientReview();
  }, [fetchClientReview]);

  useEffect(() => {
    const iCard = (sections || []).find((s) => s.section === DealRoomSection.DECIDED_TO_PURCHASE_THE_INSURANCE);
    setInsuranceCard(iCard || null);
  }, [sections]);

  useEffect(() => {
    setOfferSections(
      getFilteredOfferSectionSections(dealRoom.offerSections || []).map((o) =>
        getMappedAdvancedOfferSections({
          ...o,
          labelText: o.defaultLabel,
        }),
      ),
    );
  }, [dealRoom?.offerSections]);

  useEffect(() => {
    setMainOffer(
      (dealRoom.offerSections || [])
        .filter((o) => {
          const display = (offerSections || []).find((s) => s.file === o.file)?.display;
          return o.link && o.digitalFormPassword && display;
        })
        .map((o) => {
          const label = (offerSections || []).find((s) => s.file === o.file)?.label;
          return {
            ...o,
            label: label || null,
          };
        }),
    );
  }, [offerSections, dealRoom?.offerSections]);

  useEffect(() => {
    setMenuSections(dealRoom?.menuSections || []);
  }, [dealRoom?.menuSections]);

  useEffect(() => {
    setActiveSections(getDisplayMenuSections(sections, offerSections, menuSections, false, comparisonSections, t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, offerSections, menuSections, comparisonSections]);

  // On first load, open and scroll to the section marked default (the starred
  // entry) — unless the URL already deep-links a section, which then wins. The
  // default may be a top-level section or a sub-menu child; routing through the
  // hash reuses the deep-link flow, which opens a child's parent for it too.
  useEffect(() => {
    if (defaultAppliedRef.current || activeSections.length === 0) return;
    defaultAppliedRef.current = true;

    // A hash in the URL is handled here too, not handed to the hashchange flow:
    // that event does not arrive on a first load, so a deep link had nothing
    // driving it and the page simply stayed where it was.
    const hashKey = window.location.hash.replace('#', '');
    const defaultTop = activeSections.find((section) => section.default);
    const defaultChild = defaultTop
      ? undefined
      : activeSections.flatMap((section) => section.subMenu || []).find((sub) => sub.default);
    const landingKey = hashKey || defaultTop?.key || defaultChild?.key;
    if (!landingKey) return;

    // Keeps the landing shareable when it came from the default rather than a link.
    if (!hashKey) window.location.hash = landingKey;

    setLandingPending(true);

    const topLevel = activeSections.find((section) => section.key === landingKey);
    if (topLevel) {
      revealSection(topLevel.key);
      scrollToSection(topLevel.key, () => setLandingPending(false));
      return;
    }

    // Open the parent without asking it to scroll: openSection's request is
    // delivered through state, so it would arrive *after* the direct call below
    // and cancel it, parking the reader on the parent header rather than on the
    // section the room is meant to open at.
    const parent = activeSections.find((section) => section.subMenu?.some((sub) => sub.key === landingKey));
    if (parent) revealSection(parent.key);
    scrollToSection(landingKey, () => setLandingPending(false));
  }, [activeSections, openSection, revealSection]);

  // Documents and Comparisons are expanded on arrival, so their contents are
  // visible without a click. Deliberately not routed through openSection: these
  // are not opens the reader performed, so seeding prevOpenKeysRef keeps them
  // out of the section-view tracking above.
  useEffect(() => {
    if (defaultOpenAppliedRef.current || activeSections.length === 0) return;
    defaultOpenAppliedRef.current = true;

    const defaults = activeSections
      .filter((section) => section.key === DealRoomSection.DOCUMENTS || section.key === DealRoomSection.COMPARISONS)
      .map((section) => section.key);
    if (defaults.length === 0) return;

    prevOpenKeysRef.current = [...prevOpenKeysRef.current, ...defaults];
    setOpenKeys((keys) => [...keys, ...defaults.filter((key) => !keys.includes(key))]);
  }, [activeSections]);

  useEffect(() => {
    async function fetchOnlineUsers(dealRoomId: string) {
      try {
        const onlineUsers = user
          ? await getDealRoomOnlinePeople(dealRoomId)
          : await getDealRoomOnlinePeopleExternal(dealRoomId, {
              sessionId: dealRoom?.sessionId || null,
              uniqueId: dealRoom?.uniqueId || null,
            });
        const uniqueUsers = onlineUsers.filter(
          (user, index, self) => index === self.findIndex((u) => u._id === user._id),
        );
        setOnlineUsers(uniqueUsers);
        setVisibleUsers(uniqueUsers.slice(0, isMobile ? 1 : 5));
        setExtraUsers(uniqueUsers.slice(isMobile ? 1 : 5, uniqueUsers.length));
      } catch (error) {}
    }

    if (dealRoom?.id) {
      const connectionName = `${SOCKET_DEAL_ROOM_ONLINE_USERS}${dealRoom?.id}`;
      fetchOnlineUsers(dealRoom?.id);
      socket.emit(SOCKET_DEAL_ROOM_SYNC_ONLINE_USERS, {
        tabId: getTabId(),
      });
      socket.on(connectionName, () => {
        fetchOnlineUsers(dealRoom.id as string);
      });

      socket.on(SOCKET_DEAL_ROOM_ATTACHMENT, (data: { dealRoomId: string }) => {
        if (data.dealRoomId === dealRoom.id) {
          getUnreadFile();
        }
      });
      socket.on(SOCKET_DEAL_ROOM_UNREAD, (data: { dealRoomId: string }) => {
        if (data.dealRoomId === dealRoom.id) {
          setFaviconBadge(false);
        }
      });

      socket.on('reconnect', () => {
        socket.emit(SOCKET_DEAL_ROOM_SYNC_ONLINE_USERS, {
          tabId: getTabId(),
        });
      });

      // Clean up the listener
      return () => {
        socket.off(connectionName);
        socket.off(SOCKET_DEAL_ROOM_ATTACHMENT);
        socket.off(SOCKET_DEAL_ROOM_UNREAD);
      };
    }
  }, [
    dealRoom.channelId,
    dealRoom.groupChannelId,
    dealRoom.id,
    dealRoom?.sessionId,
    dealRoom?.uniqueId,
    getUnreadFile,
    isMobile,
    setFaviconBadge,
    user,
  ]);

  useEffect(() => {
    if (user) {
      socket.on(SOCKET_DEAL_ROOM_MESSAGE_FOR_ADMIN, (data: IDealRoomMessage) => {
        addNotification(user, isAdmin, data);
        if (data.dealRoomId === dealRoom?.id) handleUnreadMessage();
      });
      // Clean up the listener
      return () => {
        socket.off(SOCKET_DEAL_ROOM_MESSAGE_FOR_ADMIN);
      };
    }
  }, [user, isAdmin, addNotification, dealRoom?.id, handleUnreadMessage]);

  useEffect(() => {
    if (!user) return;
    const onVisibilityChange = () => {
      if (!document.hidden && hasUnreadRef.current) {
        markDealRoomRead();
        setFaviconBadge(false);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (faviconTimerRef.current) clearTimeout(faviconTimerRef.current);
      setFaviconBadge(false);
    };
  }, [user, markDealRoomRead, triggerReadTimer, setFaviconBadge]);

  useEffect(() => {
    // run on mount
    if (window.location.hash) handleHashChange(window.location.hash);

    // listen for hash changes
    const onHashChange = () => handleHashChange(window.location.hash);
    window.addEventListener('hashchange', onHashChange);

    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [handleHashChange]);

  // scrollToSection waits for the page height to settle before measuring, which
  // the fixed delay this replaced could not do now that panels start expanded.
  useEffect(() => {
    if (!scrollTarget) return;
    scrollToSection(scrollTarget.key);
  }, [scrollTarget]);

  return (
    <>
      {dealRoom && (
        <S.Wrapper>
          <Spinner
            spinning={loader || landingPending}
            tip={landingPending ? t('dealRooms.landing.openingSection', 'פותח את המקטע…') : undefined}
          >
            {menuModal && (
              <ChooseDisabledMenu
                loading={loader}
                isOpen={menuModal}
                sections={sections}
                menuSections={menuSections}
                dealRoomId={dealRoom?.id as string}
                onClose={() => setMenuModal(false)}
                onSelect={onSelectMenu}
                onRemoveMenu={onRemoveMenu}
              />
            )}
            {documentModal && (
              <DealRoomUploadFileModal
                open={documentModal}
                onCancel={() => setDocumentModal(false)}
                onSuccess={onUploadSuccess}
                dealRoomId={dealRoom?.id as string}
                sessionId={dealRoom?.sessionId as string}
                uniqueId={dealRoom?.uniqueId as string}
              />
            )}
            {isChatSidebarOpen && (
              <DealRoomChatSidebar
                dealRoom={dealRoom}
                chatMessage={chatMessage}
                defaultChannel={defaultChannel}
                open={isChatSidebarOpen}
                onClose={() => {
                  setDefaultChannel(null);
                  setChatSidebarOpen(false);
                }}
                onFileSuccess={onUploadSuccess}
              />
            )}
            {isReviewOpen && (
              <DealRoomRemarkModal
                clientReview={clientReview}
                open={isReviewOpen}
                onClose={() => setIsReviewOpen(false)}
              />
            )}
            {mouseflowModalOpen && dealRoom?.id && (
              <MouseflowDetailsModal
                open={mouseflowModalOpen}
                dealRoomId={dealRoom.id as string}
                onClose={() => setMouseflowModalOpen(false)}
              />
            )}
            {!user && people && (
              <>
                <MedianCoBrowse dealRoom={dealRoom} people={people} />
                <MouseflowWidget dealRoom={dealRoom} people={people} />
                <AccessibilityWidget />
                <TrackingScripts />
              </>
            )}
            <S.Header scrolled={scrolled}>
              <h2>
                {isMobile ? (
                  <Avatar key="logo" src={appLogo} size={40} />
                ) : (
                  <>
                    <img className="app-logo" src={mainLogo} alt="Logo" />
                  </>
                )}
              </h2>

              {user && dealRoom.status !== DealRoomStatus.OPEN_FOR_CUSTOMER && (
                <S.DealRoomStatusBadge>
                  {t('dealRooms.form.status')}: {getDealRoomStatus(t, dealRoom.status)}
                </S.DealRoomStatusBadge>
              )}

              <nav>
                {user && dealRoom?.id && (
                  <Tooltip
                    title={t(
                      dealRoom?.mouseflow?.level ? 'dealRooms.mouseflow.tooltip' : 'dealRooms.mouseflow.disabledNote',
                    )}
                    placement="bottom"
                  >
                    <S.MouseflowButton
                      $levelColor={
                        dealRoom?.mouseflow?.level ? LEVEL_COLORS[dealRoom?.mouseflow.level as LevelType] : undefined
                      }
                      onClick={() => {
                        if (dealRoom?.mouseflow?.level) {
                          setMouseflowModalOpen(true);
                        }
                      }}
                      aria-label={t('dealRooms.mouseflow.button')}
                      $disabled={!dealRoom?.mouseflow?.level}
                    >
                      <DashboardOutlined />
                      {dealRoom?.mouseflow?.level && <span className="mf-level-dot" />}
                      {dealRoom?.mouseflow?.level && (
                        <span className="mf-level-text">
                          {t(`dealRooms.mouseflow.levels.${dealRoom?.mouseflow?.level}`)}
                        </span>
                      )}
                    </S.MouseflowButton>
                  </Tooltip>
                )}
                <Tooltip title={t('dealRooms.landing.copyLinkToDealRoom')} placement="bottom">
                  <S.ShareButton onClick={shareLink}>
                    <ShareOutLineIcon />
                  </S.ShareButton>
                </Tooltip>
                {onlineUsers.length > 0 && (
                  <>
                    <Avatar.Group
                      maxCount={isMobile ? 2 : 6}
                      maxStyle={{ color: '#37352f', backgroundColor: '#b1e1ff' }}
                    >
                      {visibleUsers.map((user) => (
                        <Tooltip
                          key={user.id}
                          placement="bottom"
                          title={
                            <div>
                              <p>
                                {t('dealRooms.landing.onlineUserText', {
                                  user: user.firstName,
                                  phone: user.phone,
                                  date: Dates.format(user.createdAt as Date, DATE_TIME_FORMAT),
                                })}
                              </p>
                            </div>
                          }
                        >
                          <>
                            {user?.image ? (
                              <UserAvatar noMargin={true} image={user.image as unknown as string} size={40} />
                            ) : (
                              <S.OnlineAvatar size={40}>{user.firstName?.substr(0, 1).toUpperCase()}</S.OnlineAvatar>
                            )}
                          </>
                        </Tooltip>
                      ))}

                      {extraUsers.length > 0 && (
                        <Popover
                          placement="bottom"
                          content={
                            <div>
                              {extraUsers.map((user) => (
                                <Tooltip
                                  key={user.id}
                                  placement="bottom"
                                  title={
                                    <div>
                                      <p>
                                        {t('dealRooms.landing.onlineUserText', {
                                          user: user.firstName,
                                          email: user.email,
                                          phone: user.phone,
                                          date: Dates.format(user.createdAt as Date, DATE_TIME_FORMAT),
                                        })}
                                      </p>
                                    </div>
                                  }
                                >
                                  <>
                                    {user?.image ? (
                                      <UserAvatar image={user.image as unknown as string} size={40} />
                                    ) : (
                                      <S.OnlineAvatar size={40}>
                                        {user.firstName?.substr(0, 1).toUpperCase()}
                                      </S.OnlineAvatar>
                                    )}
                                  </>
                                </Tooltip>
                              ))}
                            </div>
                          }
                        >
                          <Avatar style={{ backgroundColor: '#b1e1ff', color: '#37352f' }}>+{extraUsers.length}</Avatar>
                        </Popover>
                      )}
                    </Avatar.Group>
                  </>
                )}
                <S.BlueButton>{getUserName()}</S.BlueButton>
              </nav>
            </S.Header>
            <div onClickCapture={onNavBarClick}>
              <SectionNavBar sections={activeSections} onChangeActiveKey={onChangeActiveKey} />
            </div>
            <S.Body scrolled={scrolled}>
              <S.SidebarWrapper scrolled={scrolled}>
                <S.SidebarContent>
                  <DealRoomMenu
                    isAdmin={!!user && !isDealRoomClosed}
                    sections={sections}
                    offerSections={offerSections}
                    menuSections={menuSections}
                    comparisonSections={comparisonSections}
                    unreadFiles={unreadFiles}
                    onUpdateSections={onUpdateSections}
                    onOpenMenuModal={setMenuModal}
                  />
                </S.SidebarContent>
              </S.SidebarWrapper>
              <S.ContentWrapper>
                <AgentBannerCard dealRoom={dealRoom} homeImg={homeImg} />
                <DealRoomMessageCard dealRoom={dealRoom} isAdmin={!!user && !isDealRoomClosed} />
                <S.DealContent onClickCapture={onAccordionContentClick}>
                  <S.ParentCollapse activeKey={openKeys} onChange={onCollapseChange}>
                    {activeSections.length > 0 &&
                      activeSections
                        .filter((section) => section.key !== DealRoomSection.DECIDED_TO_PURCHASE_THE_INSURANCE)
                        .map((section) => (
                          <Panel
                            id={section.key}
                            header={renderPanelHeader(
                              section.label
                                ? section.label
                                : section.isExternal
                                ? section.labelKey
                                : t(section.labelKey),
                              section.key,
                              section.isExternal ? section.description : undefined,
                            )}
                            key={section.key}
                          >
                            {getSectionContent(section, dealRoom, internalFiles, externalFiles)}
                          </Panel>
                        ))}
                  </S.ParentCollapse>
                </S.DealContent>
                {insuranceCard && insuranceCard.display && mainOffer?.length > 0 && (
                  <DealRoomInsuranceCard user={user} section={insuranceCard} mainOffer={mainOffer} />
                )}
              </S.ContentWrapper>
            </S.Body>
            <DealRoomFooter />
          </Spinner>

          <S.FixedBtn
            onClick={() => {
              mfTag(MF_LABELS.chatButtonClick);
              setChatSidebarOpen(true);
            }}
          >
            {t(user ? 'dealRooms.landing.chat' : 'dealRooms.landing.chatWithCustomers')}
          </S.FixedBtn>

          {clientReview && (
            <S.FixedLink
              onClick={() => {
                mfTag(MF_LABELS.reviewLinkClick);
                setIsReviewOpen(true);
              }}
            >
              {t('dealRooms.landing.customerRemarks')}
            </S.FixedLink>
          )}
        </S.Wrapper>
      )}
    </>
  );
};
